<template>
  <div class="nutrition-support-page">
    <UserAppChrome back-mode="history" />

    <main class="nutrition-support-shell">
      <UserSectionHeader
        eyebrow="Assistenza nutrizionale"
        title="I tuoi ticket"
        description="Scrivi al Team Nutrizionale e ritrova qui tutte le richieste, le risposte e gli aggiornamenti."
      />

      <button type="button" class="new-ticket-card" @click="goToCreateTicket">
        <span class="new-ticket-card__icon" aria-hidden="true">
          <span class="material-icons">add_comment</span>
        </span>
        <span class="new-ticket-card__copy">
          <small>NUOVA RICHIESTA</small>
          <strong>Apri un nuovo ticket</strong>
          <span>Raccontaci di cosa hai bisogno: il team ti risponderà direttamente nella conversazione.</span>
        </span>
        <span class="new-ticket-card__arrow material-icons" aria-hidden="true">north_east</span>
      </button>

      <section class="tickets-section" aria-labelledby="tickets-heading">
        <header class="tickets-section__heading">
          <div>
            <p>CONVERSAZIONI</p>
            <h2 id="tickets-heading">Le tue richieste</h2>
          </div>
          <span v-if="!isLoading" class="tickets-section__count">
            {{ userTickets.length }} {{ userTickets.length === 1 ? 'ticket' : 'ticket' }}
          </span>
        </header>

        <div v-if="isLoading" class="tickets-loader" role="status" aria-live="polite">
          <span class="tickets-loader__spinner" aria-hidden="true"></span>
          <strong>Caricamento conversazioni</strong>
          <small>Stiamo recuperando i tuoi ticket.</small>
        </div>

        <div v-else-if="userTickets.length === 0" class="tickets-empty">
          <span class="material-icons" aria-hidden="true">forum</span>
          <strong>Nessuna conversazione</strong>
          <p>Quando aprirai una richiesta, la troverai qui con tutti gli aggiornamenti.</p>
        </div>

        <ul v-else class="tickets-grid">
          <li v-for="(ticket, index) in userTickets" :key="ticket.id">
            <button type="button" class="ticket-card" @click="viewConversation(ticket.id)">
              <span class="ticket-card__index" aria-hidden="true">
                {{ String(index + 1).padStart(2, '0') }}
              </span>
              <span class="ticket-card__copy">
                <small>RICHIESTA NUTRIZIONALE</small>
                <strong>{{ ticket.title || 'Conversazione con il team' }}</strong>
                <time :datetime="String(ticket.lastUpdate || '')">
                  {{ formatDate(ticket.lastUpdate) }}
                </time>
              </span>
              <span class="ticket-card__actions">
                <span class="ticket-status" :class="statusClass(ticket.status)">
                  {{ ticket.status || 'Aperto' }}
                </span>
                <span class="ticket-card__arrow material-icons" aria-hidden="true">arrow_forward</span>
              </span>
            </button>
          </li>
        </ul>
      </section>
    </main>
  </div>
</template>

<script>
import { useRouter } from 'vue-router';
import { ref, onMounted } from 'vue';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import UserAppChrome from './UserAppChrome.vue';
import UserSectionHeader from './UserSectionHeader.vue';

export default {
  name: 'TicketPage',
  components: { UserAppChrome, UserSectionHeader },
  setup() {
    const userTickets = ref([]);
    const isLoading = ref(true);
    const router = useRouter();

    const goToCreateTicket = () => {
      router.push('/create-ticket');
    };

    const viewConversation = (ticketId) => {
      router.push({ name: 'UserTicketConversation', params: { ticketId } });
    };

    const parseTimestamp = (value) => {
      if (!value) return 0;
      if (typeof value?.toDate === 'function') {
        return value.toDate().getTime();
      }
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    const fetchTickets = async () => {
      isLoading.value = true;
      const auth = getAuth();
      const currentUserId = auth.currentUser?.uid;
      if (!currentUserId) {
        console.error('Utente non autenticato');
        isLoading.value = false;
        return;
      }

      userTickets.value = [];

      try {
        const db = getFirestore();
        const q = query(collection(db, 'tickets'), where('userId', '==', currentUserId));
        const querySnapshot = await getDocs(q);

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const conversations = Array.isArray(data.conversations) ? data.conversations : [];
          const lastUpdate = conversations[conversations.length - 1]?.timestamp || data.timestamp || null;

          userTickets.value.push({
            id: doc.id,
            ...data,
            lastUpdate,
          });
        });

        // Ticket nuovi in alto
        userTickets.value.sort((a, b) => parseTimestamp(b.lastUpdate) - parseTimestamp(a.lastUpdate));
      } catch (error) {
        console.error('Errore durante il recupero ticket:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const formatDate = (timestamp) => {
      if (!timestamp) return 'N/A';
      const date = typeof timestamp?.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    };

    const statusClass = (status = '') => {
      if (status === 'Risposto') return 'ticket-status--answered';
      if (status === 'Non Risposto') return 'ticket-status--waiting';
      if (status === 'Completato') return 'ticket-status--complete';
      return 'ticket-status--open';
    };

    onMounted(() => {
      const auth = getAuth();
      onAuthStateChanged(auth, (user) => {
        if (user) {
          fetchTickets();
        } else {
          isLoading.value = false;
          console.error('Utente non autenticato');
        }
      });
    });

    return {
      userTickets,
      goToCreateTicket,
      viewConversation,
      formatDate,
      statusClass,
      isLoading,
    };
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Montserrat:wght@700;900&display=swap');

.nutrition-support-page {
  --ff-bg: #f5f4f0;
  --ff-white: #ffffff;
  --ff-black: #111110;
  --ff-muted: #66635f;
  --ff-red: #c8102e;
  --ff-red-dark: #990b22;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
  color: var(--ff-black);
  background:
    radial-gradient(circle at 92% 8%, rgba(200, 16, 46, 0.1), transparent 25rem),
    radial-gradient(circle at 3% 74%, rgba(200, 16, 46, 0.05), transparent 28rem),
    var(--ff-bg);
  font-family: 'DM Sans', sans-serif;
  text-align: left;
}

.nutrition-support-page,
.nutrition-support-page *,
.nutrition-support-page *::before,
.nutrition-support-page *::after {
  box-sizing: border-box;
}

.nutrition-support-page::before {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  content: '';
  opacity: 0.24;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.08'/%3E%3C/svg%3E");
}

.nutrition-support-shell {
  width: min(100%, 1160px);
  margin: 0 auto;
  padding: clamp(34px, 6vw, 72px) clamp(20px, 5vw, 52px) 148px;
}

.new-ticket-card {
  position: relative;
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) 42px;
  gap: 16px;
  align-items: center;
  width: 100%;
  margin: 0 0 clamp(44px, 7vw, 72px);
  padding: 20px;
  overflow: hidden;
  color: var(--ff-white);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 24px;
  background:
    radial-gradient(circle at 86% 0, rgba(200, 16, 46, 0.54), transparent 18rem),
    linear-gradient(140deg, #1c1c1a, #0e0e0d);
  box-shadow: 0 22px 48px rgba(17, 17, 16, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.11);
  font: inherit;
  text-align: left;
  cursor: pointer;
  appearance: none;
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.new-ticket-card::after {
  position: absolute;
  right: 20px;
  bottom: 0;
  left: 20px;
  height: 3px;
  content: '';
  border-radius: 999px 999px 0 0;
  background: linear-gradient(90deg, var(--ff-red), #ff3157, transparent 88%);
}

.new-ticket-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 28px 58px rgba(17, 17, 16, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.new-ticket-card__icon,
.new-ticket-card__arrow {
  display: grid;
  place-items: center;
  border-radius: 50%;
}

.new-ticket-card__icon {
  width: 56px;
  height: 56px;
  border: 1px solid rgba(255, 255, 255, 0.17);
  background: rgba(255, 255, 255, 0.08);
}

.new-ticket-card__icon .material-icons {
  color: var(--ff-white);
  font-size: 25px;
}

.new-ticket-card__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.new-ticket-card__copy small {
  color: #ff4869;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.16em;
}

.new-ticket-card__copy strong {
  font-size: clamp(1.08rem, 2vw, 1.3rem);
  line-height: 1.2;
}

.new-ticket-card__copy > span {
  max-width: 660px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.82rem;
  line-height: 1.45;
}

.new-ticket-card__arrow {
  width: 42px;
  height: 42px;
  color: var(--ff-white);
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  font-size: 20px;
}

.tickets-section__heading {
  display: flex;
  gap: 18px;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}

.tickets-section__heading p {
  margin: 0 0 6px;
  color: var(--ff-red);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.18em;
}

.tickets-section__heading h2 {
  margin: 0;
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(1.65rem, 4vw, 2.45rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.055em;
  text-transform: uppercase;
}

.tickets-section__count {
  flex: 0 0 auto;
  padding: 7px 11px;
  color: var(--ff-muted);
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.6);
  font-size: 0.6875rem;
  font-weight: 700;
}

.tickets-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tickets-grid > li {
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
}

.ticket-card {
  position: relative;
  display: grid;
  grid-template-columns: 50px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  width: 100%;
  min-height: 118px;
  margin: 0;
  padding: 17px;
  overflow: hidden;
  color: var(--ff-black);
  border: 1px solid rgba(255, 255, 255, 0.94);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 14px 34px rgba(47, 38, 32, 0.09), inset 0 1px 0 rgba(255, 255, 255, 0.96);
  font: inherit;
  text-align: left;
  cursor: pointer;
  appearance: none;
  backdrop-filter: blur(18px) saturate(1.35);
  -webkit-backdrop-filter: blur(18px) saturate(1.35);
  transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}

.ticket-card::before {
  position: absolute;
  top: 0;
  right: 22px;
  left: 22px;
  height: 2px;
  content: '';
  background: linear-gradient(90deg, transparent, var(--ff-red), transparent);
}

.ticket-card:hover {
  border-color: rgba(200, 16, 46, 0.2);
  box-shadow: 0 20px 42px rgba(47, 38, 32, 0.13), inset 0 1px 0 rgba(255, 255, 255, 0.96);
  transform: translateY(-2px);
}

.ticket-card__index {
  display: grid;
  width: 50px;
  height: 50px;
  place-items: center;
  color: var(--ff-red);
  border: 1px solid rgba(200, 16, 46, 0.14);
  border-radius: 15px;
  background: rgba(200, 16, 46, 0.07);
  font-size: 0.82rem;
  font-weight: 800;
}

.ticket-card__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.ticket-card__copy small {
  color: #8b8781;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.ticket-card__copy strong {
  overflow: hidden;
  font-size: 0.98rem;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-card__copy time {
  color: var(--ff-muted);
  font-size: 0.7rem;
}

.ticket-card__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
}

.ticket-status {
  display: inline-flex;
  align-items: center;
  min-height: 25px;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.ticket-status--answered {
  color: #1d6b3a;
  background: #dff4e6;
}

.ticket-status--waiting {
  color: #7c5a00;
  background: #fff0bc;
}

.ticket-status--open {
  color: var(--ff-red-dark);
  background: rgba(200, 16, 46, 0.1);
}

.ticket-status--complete {
  color: #5d5a55;
  background: #e9e7e2;
}

.ticket-card__arrow {
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  color: var(--ff-white);
  border-radius: 50%;
  background: var(--ff-black);
  font-size: 16px;
}

.tickets-loader,
.tickets-empty {
  display: flex;
  min-height: 190px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px;
  color: var(--ff-muted);
  border: 1px solid rgba(255, 255, 255, 0.92);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.64);
  box-shadow: 0 16px 38px rgba(47, 38, 32, 0.08);
  text-align: center;
}

.tickets-loader strong,
.tickets-empty strong {
  margin-top: 12px;
  color: var(--ff-black);
  font-size: 0.98rem;
}

.tickets-loader small,
.tickets-empty p {
  margin: 4px 0 0;
  font-size: 0.78rem;
}

.tickets-loader__spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(200, 16, 46, 0.13);
  border-top-color: var(--ff-red);
  border-radius: 50%;
  animation: tickets-spin 0.8s linear infinite;
}

.tickets-empty > .material-icons {
  color: var(--ff-red);
  font-size: 30px;
}

.nutrition-support-page :is(button, a):focus-visible {
  outline: 3px solid rgba(200, 16, 46, 0.28);
  outline-offset: 3px;
}

@keyframes tickets-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 760px) {
  .nutrition-support-shell {
    padding: 28px 12px 138px;
  }

  .new-ticket-card {
    grid-template-columns: 48px minmax(0, 1fr) 34px;
    gap: 12px;
    margin-bottom: 42px;
    padding: 15px;
    border-radius: 20px;
  }

  .new-ticket-card__icon {
    width: 48px;
    height: 48px;
  }

  .new-ticket-card__copy > span {
    display: none;
  }

  .new-ticket-card__arrow {
    width: 34px;
    height: 34px;
    font-size: 17px;
  }

  .tickets-section {
    padding: 0 7px;
  }

  .tickets-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 11px;
  }

  .ticket-card {
    grid-template-columns: 44px minmax(0, 1fr) auto;
    gap: 11px;
    min-height: 104px;
    padding: 14px;
    border-radius: 18px;
  }

  .ticket-card__index {
    width: 44px;
    height: 44px;
    border-radius: 13px;
  }

  .ticket-card__actions {
    gap: 8px;
  }
}

@media (max-width: 390px) {
  .ticket-card {
    grid-template-columns: 42px minmax(0, 1fr) 30px;
  }

  .ticket-card__index {
    width: 42px;
    height: 42px;
  }

  .ticket-card__actions {
    align-items: center;
  }

  .ticket-status {
    position: absolute;
    top: 11px;
    right: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .new-ticket-card,
  .ticket-card {
    transition: none;
  }

  .tickets-loader__spinner {
    animation: none;
  }
}
</style>
